import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, OnDestroy, Optional } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ResolveEnd, Router } from '@angular/router';
import { ValidationResult } from '@foundation/foundation-core-sdk/src/lib/dtos/validation-result';
import { BehaviorSubject, EMPTY, Observable, ReplaySubject, Subject, takeUntil } from 'rxjs';
import { EpdkErrorDialogComponent } from '../../ui/error-dialog/error-dialog.component';
import { EpdkErrorSnackBarComponent } from '../../ui/error-snack-bar/error-snack-bar.component';

export interface ErrorPageParams {
  title?: string,
  message?: string;
}

export interface ErrorDialogData {
  title: string;
  errorMessages: string[]
}

export interface ClientErrorReporter {
  pushError(error: unknown, attributes?: Record<string, string>): void;
}

export const CLIENT_ERROR_REPORTER = new InjectionToken<ClientErrorReporter>('CLIENT_ERROR_REPORTER');

@Injectable({ providedIn: 'root' })
export class ErrorService implements OnDestroy {

  readonly errorPage$: Observable<ErrorPageParams | null>;

  readonly dialogClosed$: Observable<ErrorDialogData> = new Subject<ErrorDialogData>();

  private _currentDialogRef?: MatDialogRef<EpdkErrorDialogComponent>;

  private _errorPageSubject$ = new BehaviorSubject<ErrorPageParams | null>(null);

  private readonly _destroy$ = new ReplaySubject<void>(1);

  constructor(
    private _router: Router,
    private _snackBar: MatSnackBar,
    private _dialog: MatDialog,
    @Optional() @Inject(CLIENT_ERROR_REPORTER) private _clientErrorReporter: ClientErrorReporter | null = null) {

    this.errorPage$ = this._errorPageSubject$.asObservable();

    this._router.events.pipe(takeUntil(this._destroy$)).subscribe(e => {
      if (e instanceof ResolveEnd) {
        this.hideErrorPage();
      }
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  };

  showErrorPage(errorPageParams: ErrorPageParams = {}) {
    this._errorPageSubject$.next(errorPageParams);
  }

  hideErrorPage() {
    this._errorPageSubject$.next(null);
  }

  handleHttpError(httpErrorResponse: HttpErrorResponse): typeof EMPTY {
    this._reportClientError(httpErrorResponse);

    if (httpErrorResponse.status == 403) {

      this.showSnackBar('You do not have permission to access this resource.');

      return EMPTY;
    }

    if (httpErrorResponse.status >= 400 && httpErrorResponse.status < 500) {
      this.openDialog(this._getClientErrorMessages(httpErrorResponse.error));

      return EMPTY;
    }

    if (httpErrorResponse.status >= 500) {

      this.showSnackBar('An internal error has occurred.');

      return EMPTY;
    }

    this.showSnackBar();

    return EMPTY;
  }

  showSnackBar(errorMessage: string = ''): void {
    this._snackBar.openFromComponent(EpdkErrorSnackBarComponent, {
      duration: 3500,
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'error-snack-bar',
      data: { message: errorMessage }
    });
  }

  openDialog(errorMessages: string[], title: string = ''): void {
    if (this._currentDialogRef) {
      return;
    }

    this._currentDialogRef = this._dialog.open<EpdkErrorDialogComponent, ErrorDialogData>(EpdkErrorDialogComponent, {
      width: '464px',
      data: { title, errorMessages },
      autoFocus: false,
      restoreFocus: false
    });

    this._currentDialogRef.afterClosed().subscribe(_ => {
      this._currentDialogRef = null;
      (this.dialogClosed$ as Subject<ErrorDialogData>).next({ title, errorMessages });
    });
  }

  closeDialog() {
    if (!this._currentDialogRef) {
      return;
    }

    this._currentDialogRef.close();
  }

  private _reportClientError(error: unknown): void {
    if (!this._clientErrorReporter) {
      return;
    }

    try {
      const attributes: Record<string, string> = {};
      if (error instanceof HttpErrorResponse) {
        attributes.httpStatus = String(error.status ?? 0);
        attributes.statusText = error.statusText ?? '';
        if (error.url) {
          attributes.url = error.url;
        }
      }
      this._clientErrorReporter.pushError(error, attributes);
    } catch {
      // Reporting must never break the user-facing error path.
    }
  }

  // ServiceResponse.errors is ValidationResult[]; ASP.NET model-binding 400s use ProblemDetails
  // where errors is a field → string[] dictionary. Calling .map on that object throws.
  private _getClientErrorMessages(error: unknown): string[] {
    if (typeof error === 'string' && error) {
      return [error];
    }

    if (!error || typeof error !== 'object') {
      return [];
    }

    const body = error as { errors?: unknown; title?: string; errorMessage?: string };
    const errors = body.errors;

    if (Array.isArray(errors)) {
      return errors.map((validationResult: ValidationResult) => {
        let errorMessage = validationResult?.errorMessage ?? '';

        if (validationResult?.memberNames?.length > 0) {
          errorMessage += ` (${validationResult.memberNames.join('/')})`;
        }

        return errorMessage;
      }).filter(message => !!message);
    }

    if (errors && typeof errors === 'object') {
      const messages: string[] = [];
      Object.values(errors as Record<string, unknown>).forEach(value => {
        const values = Array.isArray(value) ? value : [value];
        values.forEach(item => {
          if (typeof item === 'string' && item) {
            messages.push(item);
          }
        });
      });
      if (messages.length > 0) {
        return messages;
      }
    }

    if (typeof body.errorMessage === 'string' && body.errorMessage) {
      return [body.errorMessage];
    }

    if (typeof body.title === 'string' && body.title) {
      return [body.title];
    }

    return [];
  }
}
